import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import Paywall, { PricingPlans } from "@/components/Paywall";

/* Pricing as a linkable route — opens the plans modal over a clean page */
const PricingPage = () => {
  const navigate = useNavigate();
  return (
    <div data-testid="pricing-page" className="min-h-screen bg-[#F8FAF7]">
      <Paywall
        open
        onClose={() =>
          window.history.length > 1 ? navigate(-1) : navigate("/")
        }
      />
    </div>
  );
};
import { ArrowUp, Loader2 } from "lucide-react";
import Signup from "@/pages/Signup";
import { apiFetch, getToken, setToken } from "@/api";
import PageBlobs from "@/components/PageBlobs";
import SiteFooter from "@/components/SiteFooter";
import SiteHeader from "@/components/landing/SiteHeader";
import HeroSection from "@/components/landing/HeroSection";
import ProductPreviewSection from "@/components/landing/ProductPreviewSection";
import {
  PracticeLearnSection,
  AskAiSection,
  StudyPlannerSection,
  ProgressSection,
} from "@/components/landing/ShowcaseSections";
import TrustSection from "@/components/landing/TrustSection";
import TestimonialsSection from "@/components/landing/TestimonialsSection";
import FinalCtaSection from "@/components/landing/FinalCtaSection";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import RefundCancellation from "@/pages/RefundCancellation";
import Contact from "@/pages/Contact";
import Dashboard from "@/pages/Dashboard";
import PracticeSetup from "@/pages/PracticeSetup";
import LearnVideos from "@/pages/LearnVideos";
import Practice from "@/pages/Practice";
import PracticeComplete from "@/pages/PracticeComplete";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminPyq from "@/pages/AdminPyq";
import PyqLibrary from "@/pages/PyqLibrary";
import PromptLearning from "@/pages/PromptLearning";
import Progress from "@/pages/Progress";
import Onboarding from "@/pages/Onboarding";
import StudyPlanner from "@/pages/StudyPlanner";
import PracticeLauncher from "@/pages/PracticeLauncher";
import ExamSetup from "@/pages/ExamSetup";
import ExamPaper from "@/pages/ExamPaper";
import { Toaster } from "@/components/ui/sonner";

const MagneticButtons = () => {
  useEffect(() => {
    // Desktop only: touch devices (phones/tablets) get plain buttons
    if (!window.matchMedia("(pointer: fine) and (hover: hover)").matches)
      return;
    const STRENGTH = 0.18;
    const MAX_OFFSET = 10;
    const clamp = (v) => Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, v));
    const onMove = (e) => {
      const el = e.target?.closest?.('[data-magnetic="true"]');
      if (!el) return;
      const r = el.getBoundingClientRect();
      const x = clamp((e.clientX - (r.left + r.width / 2)) * STRENGTH);
      const y = clamp((e.clientY - (r.top + r.height / 2)) * STRENGTH);
      el.style.transition = "transform 0.05s linear";
      el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
    };
    const onOut = (e) => {
      const el = e.target?.closest?.('[data-magnetic="true"]');
      if (!el) return;
      if (e.relatedTarget && el.contains(e.relatedTarget)) return;
      el.style.transition =
        "transform 0.32s cubic-bezier(0.34, 1.56, 0.64, 1)";
      el.style.transform = "translate(0px, 0px)";
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseout", onOut);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseout", onOut);
    };
  }, []);
  return null;
};

const ScrollTopButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 120);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <button
      data-testid="scroll-top-btn"
      data-magnetic="true"
      aria-label="Scroll to top"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className={`fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-[#16A34A] text-white shadow-sm transition-[opacity,transform] duration-300 ${
        visible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-4 opacity-0"
      }`}
    >
      <ArrowUp className="h-5 w-5" />
    </button>
  );
};

/* Thin editorial hairline used between major homepage sections */
const SectionDivider = () => (
  <hr
    data-testid="section-divider"
    className="mx-auto my-0 max-w-6xl border-t border-[#E2E8F0] opacity-70"
  />
);

const Landing = () => {
  const [loggedIn, setLoggedIn] = useState(false);

  useEffect(() => {
    if (!getToken()) return;
    apiFetch("/me")
      .then(() => setLoggedIn(true))
      .catch(() => setLoggedIn(false));
  }, []);

  return (
    <div
      data-testid="landing-page"
      id="top"
      className="relative min-h-screen overflow-x-clip bg-[#F8FAF7] font-body text-[#17201A]"
    >
      <PageBlobs />
      <ScrollTopButton />
      <SiteHeader loggedIn={loggedIn} />

      {/* Hero */}
      <HeroSection loggedIn={loggedIn} />

      {/* Product preview — see how Abhyas works */}
      <ProductPreviewSection />

      <SectionDivider />

      {/* Practice & Learn */}
      <PracticeLearnSection />

      <SectionDivider />

      {/* Ask Abhyas AI */}
      <AskAiSection />

      <SectionDivider />

      {/* Study Planner */}
      <StudyPlannerSection />

      <SectionDivider />

      {/* Personalized Progress */}
      <ProgressSection />

      {/* Why Abhyas — built on trust */}
      <TrustSection />

      {/* Testimonials */}
      <TestimonialsSection />

      <SectionDivider />

      {/* Final CTA */}
      <FinalCtaSection loggedIn={loggedIn} />

      {/* Pricing — same plans as the paywall, right on the homepage (no Free card in public view) */}
      <section
        id="pricing"
        data-testid="pricing-section"
        className="relative mx-auto w-[94%] max-w-[1520px] scroll-mt-24 px-1 pb-28 pt-4"
      >
        <p className="mb-3">
          <span data-testid="section-label-pricing" className="inline-flex items-center gap-2 rounded-lg border border-[#16A34A]/15 bg-[#16A34A]/8 px-3.5 py-1.5 text-[13px] font-bold uppercase tracking-[0.08em] text-[#14532D] sm:text-[14px] lg:text-[15px]">
            <span className="pulse-dot inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#16A34A]" />
            Pricing
          </span>
        </p>
        <h2 data-testid="pricing-section-headline" className="mb-3 font-display text-[36px] font-bold leading-[1.15] tracking-tight sm:text-[48px] lg:text-[56px]">
          Transparent, affordable pricing for{" "}
          <span className="text-[#16A34A]">every student.</span>
        </h2>
        <p className="mb-10 max-w-xl font-body text-[17px] leading-[1.6] text-[#647067] sm:text-[19px]">
          Get full access to practice, video lessons and Ask Abhyas AI with
          simple plans — no hidden fees.
        </p>
        <div data-testid="public-pricing-container">
          <PricingPlans onActivated={() => {}} hideFree />
        </div>
      </section>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }
    apiFetch("/auth/google/session", {
      method: "POST",
      body: { session_id: sessionId },
    })
      .then((data) => {
        setToken(data.token);
        window.history.replaceState(null, "", window.location.pathname);
        navigate(data.user?.board ? "/dashboard" : "/onboarding", {
          replace: true,
        });
      })
      .catch(() => navigate("/login", { replace: true }));
  }, [location.hash, navigate]);

  return (
    <div
      data-testid="auth-callback"
      className="flex min-h-screen items-center justify-center bg-[#F8FAF7] font-body"
    >
      <Loader2 className="h-8 w-8 animate-spin text-[#16A34A]" />
    </div>
  );
};

const GoogleCodeCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const state = params.get("state");
    const expected = sessionStorage.getItem("google_oauth_state");
    sessionStorage.removeItem("google_oauth_state");
    if (!code || !state || state !== expected) {
      navigate("/login", { replace: true });
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    apiFetch("/auth/google/exchange", {
      method: "POST",
      body: {
        code,
        redirect_uri: window.location.origin + "/auth/google/callback",
      },
    })
      .then((data) => {
        setToken(data.token);
        window.history.replaceState(null, "", window.location.pathname);
        navigate(data.user?.board ? "/dashboard" : "/onboarding", {
          replace: true,
        });
      })
      .catch(() => navigate("/login", { replace: true }));
  }, [location.search, navigate]);

  return (
    <div
      data-testid="google-code-callback"
      className="flex min-h-screen items-center justify-center bg-[#F8FAF7] font-body"
    >
      <Loader2 className="h-8 w-8 animate-spin text-[#16A34A]" />
    </div>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const AppRoutes = () => {
  const location = useLocation();
  // Detect Google OAuth session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/google/callback" element={<GoogleCodeCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/refund-cancellation" element={<RefundCancellation />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/practice-setup" element={<PracticeSetup />} />
      <Route path="/learn/videos" element={<LearnVideos />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/practice" element={<Practice />} />
      <Route path="/practice-complete" element={<PracticeComplete />} />
      <Route path="/progress" element={<Progress />} />
      <Route path="/planner" element={<StudyPlanner />} />
      <Route path="/smart-practice" element={<PracticeLauncher mode="smart" />} />
      <Route path="/reattempt" element={<PracticeLauncher mode="reattempt" />} />
      <Route path="/exam-mode" element={<ExamSetup />} />
      <Route path="/exam" element={<ExamPaper />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/pyq" element={<AdminPyq />} />
      <Route path="/pyq" element={<PyqLibrary />} />
      <Route path="/abhyas-ai" element={<PromptLearning />} />
      <Route path="/prompt-learning" element={<PromptLearning />} />
    </Routes>
    </>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <MagneticButtons />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </div>
  );
}

export default App;
